/**
 * MyContent: Profile tab that lists the current user's posts, ads, properties, and followings.
 * Uses list APIs with mine=true / followed. Handles loading, error, and empty states per section.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import postService from '../../../services/postService';
import { adService } from '../../../services/api';
import propertyService from '../../../services/propertyService';
import { societyService } from '../../../services/societyService';
import { getPostUrl, getAdUrl, getPropertyUrl, getSocietyUrl } from '../../../utils/urlHelpers';
import LoadingSpinner from '../../common/LoadingSpinner';

const PAGE_SIZE = 10;

const SectionList = ({ title, items, loading, error, onRetry, emptyMessage, onItemClick, getItemTitle, getItemSubtitle }) => {
  return (
    <section className="mb-10" aria-labelledby={`section-${title.replace(/\s+/g, '-').toLowerCase()}`}>
      <h2 id={`section-${title.replace(/\s+/g, '-').toLowerCase()}`} className="text-lg font-semibold text-gray-900 mb-4">
        {title}
      </h2>
      {loading && (
        <div className="flex justify-center py-6">
          <LoadingSpinner />
        </div>
      )}
      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 p-4">
          <p className="text-sm text-red-700">{error}</p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="mt-2 text-sm font-medium text-red-800 hover:text-red-900 underline"
            >
              Try again
            </button>
          )}
        </div>
      )}
      {!loading && !error && (!items || items.length === 0) && (
        <p className="text-gray-500 text-sm">{emptyMessage}</p>
      )}
      {!loading && !error && items && items.length > 0 && (
        <ul className="divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
          {items.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => onItemClick && onItemClick(item)}
                className="block w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
              >
                <span className="font-medium text-gray-900">{getItemTitle ? getItemTitle(item) : item.title}</span>
                {getItemSubtitle && (
                  <span className="block text-sm text-gray-500 mt-0.5">{getItemSubtitle(item)}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

/**
 * Followings section: lists societies the user follows with unfollow action per item.
 */
const FollowingsSection = ({
  societies,
  loading,
  error,
  onRetry,
  onUnfollow,
  unfollowingId,
  onItemClick,
}) => {
  const emptyMessage = 'You are not following any societies yet.';
  return (
    <section className="mb-10" aria-labelledby="section-followings">
      <h2 id="section-followings" className="text-lg font-semibold text-gray-900 mb-4">
        Followings
      </h2>
      {loading && (
        <div className="flex justify-center py-6">
          <LoadingSpinner />
        </div>
      )}
      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 p-4">
          <p className="text-sm text-red-700">{error}</p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="mt-2 text-sm font-medium text-red-800 hover:text-red-900 underline"
            >
              Try again
            </button>
          )}
        </div>
      )}
      {!loading && !error && (!societies || societies.length === 0) && (
        <p className="text-gray-500 text-sm">{emptyMessage}</p>
      )}
      {!loading && !error && societies && societies.length > 0 && (
        <ul className="divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
          {societies.map((society) => (
            <li key={society.id} className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-gray-50 transition-colors">
              <button
                type="button"
                onClick={() => onItemClick && onItemClick(society)}
                className="flex-1 min-w-0 text-left"
              >
                <span className="font-medium text-gray-900 block truncate">{society.name}</span>
                {society.city && (
                  <span className="text-sm text-gray-500 block truncate">{society.city}</span>
                )}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onUnfollow && onUnfollow(society);
                }}
                disabled={unfollowingId === society.id}
                className="shrink-0 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 border border-red-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {unfollowingId === society.id ? 'Unfollowing…' : 'Unfollow'}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

const MyContent = () => {
  const navigate = useNavigate();

  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [postsError, setPostsError] = useState(null);

  const [ads, setAds] = useState([]);
  const [adsLoading, setAdsLoading] = useState(true);
  const [adsError, setAdsError] = useState(null);

  const [properties, setProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(true);
  const [propertiesError, setPropertiesError] = useState(null);

  const [followings, setFollowings] = useState([]);
  const [followingsLoading, setFollowingsLoading] = useState(true);
  const [followingsError, setFollowingsError] = useState(null);
  const [unfollowingId, setUnfollowingId] = useState(null);

  const loadPosts = useCallback(async () => {
    setPostsLoading(true);
    setPostsError(null);
    try {
      const response = await postService.getPosts({ mine: true, page_size: PAGE_SIZE, ordering: '-created_at' });
      setPosts(response.results || response || []);
    } catch (err) {
      console.error('Error loading my posts:', err);
      setPostsError('Failed to load your posts.');
    } finally {
      setPostsLoading(false);
    }
  }, []);

  const loadAds = useCallback(async () => {
    setAdsLoading(true);
    setAdsError(null);
    try {
      const response = await adService.getAds({ mine: true, page_size: PAGE_SIZE });
      setAds(response.results || response || []);
    } catch (err) {
      console.error('Error loading my ads:', err);
      setAdsError('Failed to load your ads.');
    } finally {
      setAdsLoading(false);
    }
  }, []);

  const loadProperties = useCallback(async () => {
    setPropertiesLoading(true);
    setPropertiesError(null);
    try {
      const response = await propertyService.getProperties({ mine: true, page_size: PAGE_SIZE });
      const data = response.results || response;
      setProperties(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading my properties:', err);
      setPropertiesError('Failed to load your properties.');
    } finally {
      setPropertiesLoading(false);
    }
  }, []);

  const loadFollowings = useCallback(async () => {
    setFollowingsLoading(true);
    setFollowingsError(null);
    try {
      const data = await societyService.getFollowedSocieties();
      const list = data?.societies ?? [];
      setFollowings(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error loading followings:', err);
      setFollowingsError('Failed to load your followings.');
    } finally {
      setFollowingsLoading(false);
    }
  }, []);

  const handleUnfollow = useCallback(async (society) => {
    if (!society?.id) return;
    setUnfollowingId(society.id);
    try {
      await societyService.unfollowSociety(society.id);
      setFollowings((prev) => prev.filter((s) => s.id !== society.id));
    } catch (err) {
      console.error('Error unfollowing society:', err);
      setFollowingsError(err.message || 'Failed to unfollow. Please try again.');
    } finally {
      setUnfollowingId(null);
    }
  }, []);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  useEffect(() => {
    loadAds();
  }, [loadAds]);

  useEffect(() => {
    loadProperties();
  }, [loadProperties]);

  useEffect(() => {
    loadFollowings();
  }, [loadFollowings]);

  return (
    <div>
      <p className="text-gray-600 mb-6">Your posts, ads, property listings, and followings in one place.</p>

      <FollowingsSection
        societies={followings}
        loading={followingsLoading}
        error={followingsError}
        onRetry={loadFollowings}
        onUnfollow={handleUnfollow}
        unfollowingId={unfollowingId}
        onItemClick={(society) => navigate(getSocietyUrl(society))}
      />

      <SectionList
        title="My Posts"
        items={posts}
        loading={postsLoading}
        error={postsError}
        onRetry={loadPosts}
        emptyMessage="You have not created any posts yet."
        onItemClick={(item) => navigate(getPostUrl(item, item.society?.slug))}
        getItemTitle={(item) => item.title}
        getItemSubtitle={(item) => item.society?.name && `Society: ${item.society.name}`}
      />
      <SectionList
        title="My Ads"
        items={ads}
        loading={adsLoading}
        error={adsError}
        onRetry={loadAds}
        emptyMessage="You have not created any ads yet."
        onItemClick={(item) => navigate(getAdUrl(item))}
        getItemTitle={(item) => item.title}
        getItemSubtitle={(item) => item.category && `Category: ${item.category}`}
      />
      <SectionList
        title="My Properties"
        items={properties}
        loading={propertiesLoading}
        error={propertiesError}
        onRetry={loadProperties}
        emptyMessage="You have not listed any properties yet."
        onItemClick={(item) => navigate(getPropertyUrl(item))}
        getItemTitle={(item) => item.title}
        getItemSubtitle={(item) => item.property_type && `Type: ${item.property_type}`}
      />
    </div>
  );
};

export default MyContent;
